import type { User } from "@freightsimple/generated-dashboard-openapi-client/models/User";

interface DeletionBlockedReasonInput {
  /** The user someone is about to delete. */
  user: User;
  /** Everyone on the company account, as Manage Users lists them. */
  users: User[];
  /** The signed-in user's id, when the store has one. */
  signedInUserId: string | undefined;
}

/**
 * Why a user can't be deleted, or undefined when deleting is allowed.
 *
 * The own-user rule is the one the API enforces too (a 409 from
 * UsersController.deleteUser). The last-user rule is this screen's alone: a
 * customer can only reach it by being the sole user, where the own-user rule
 * already covers them, so its real job is the list FreightSimple support sees
 * — they shadow-join a company without appearing in its user list, so nothing
 * else would stop them emptying an account.
 *
 * Last-user is checked first, so someone alone on an account gets the reason
 * that tells them what to do about it rather than "that's you".
 *
 * Only users who belong to this company count towards that rule. The list also
 * carries linked-company users who can switch in, and they can't be deleted
 * from here — counting them would let support empty an account of its own
 * people while a borrowed row kept the count above one.
 */
export function viewCompanyDeletionBlockedReason(
  input: DeletionBlockedReasonInput,
): string | undefined {
  const accountMembers = input.users.filter((u) => u.belongsToThisCompany);

  if (accountMembers.length <= 1) {
    return "This is the only user on the account, so it can't be deleted. Invite someone else first.";
  }

  if (input.user.userId === input.signedInUserId) {
    return "This is your own user, so you can't delete it. Ask another user on the account to do it.";
  }

  return undefined;
}
